import { api } from './api'
import { ApiError, toApiError, apiErrorMessage } from './apiError'
import axios from 'axios'
import { cacheManager } from '../utils/cache'
import { photoImageCache } from '../utils/cache'
import { photoDb, isUrlsFresh, syncStatesEqual, type PhotoSyncState } from '../utils/photoDb'

// API base URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://deepwind-backend.vercel.app/api'

// ---------------------------------------------------------------------------
// Hata normalizasyonu
//
// Bu dosya iki farklı çağrı biçimi kullanıyor: `api.*` (ApiError fırlatır) ve ham
// `axios.*` (AxiosError fırlatır). Eski kod her ikisinde de `error.response?.data?.message`
// okuyordu; ApiError'da `.response` olmadığı için backend'in TR mesajı sessizce
// kayboluyor ve yerine jenerik metin geçiyordu. `toApiError(error).message` her iki
// biçimi de tek noktada çözer: önce backend mesajı, yoksa hata türüne göre TR metin.
// (S3'ün XML gövdeli hataları toApiError içinde reddedilir; ham XML kullanıcıya sızmaz.)
// ---------------------------------------------------------------------------

/**
 * Çoklu dosya yüklemede hangi dosyanın patladığı bilgisini kaybetmeden hatayı
 * yeniden fırlatır. Hata türü (kind) korunur ki sunum katmanı doğru metni seçebilsin.
 */
const withFileName = (fileName: string, error: unknown): ApiError => {
  const apiError = toApiError(error)
  return new ApiError(apiError.kind, {
    status: apiError.status,
    code: apiError.code,
    serverMessage: `${fileName}: ${apiError.message}`,
    details: apiError.details
  })
}

/**
 * Wasabi/S3'e presigned URL ile yapılan PUT hatalarını kullanıcı diline çevirir.
 *
 * Süresi dolmuş bir presigned URL, Wasabi'den 403 + XML gövde (SignatureDoesNotMatch)
 * döner. toApiError XML'i bilerek reddettiği için kind 'permission' olur; ama
 * kullanıcıya "bu işlem için yetkiniz yok" demek YANILTICIDIR - gerçek sebep adresin
 * süresinin dolmasıdır ve çözümü işlemi tekrar denemektir.
 *
 * 413 (gövde limiti) hariç tutulur: onun sebebi gerçekten dosya boyutudur ve
 * çağıranlar bunu `uploadErrorMessage` ile ayrıca ele alır.
 */
const toStoragePutError = (error: unknown, retryMessage: string): ApiError => {
  const putError = toApiError(error)
  if (putError.status === 413) return putError
  if (putError.kind === 'permission' || putError.kind === 'validation') {
    return new ApiError('server', {
      status: putError.status,
      serverMessage: retryMessage,
      details: putError.details
    })
  }
  return putError
}

/** 413 = gövde limiti (Vercel/S3). kind 'validation' gelir ama sebep dosya boyutudur. */
const uploadErrorMessage = (error: unknown, tooLargeMessage: string): string => {
  const apiError = toApiError(error)
  if (apiError.status === 413) return tooLargeMessage
  return apiError.message
}

// Memory cache for frequently accessed data
const memoryCache = new Map<string, { data: any, expires: number }>()

const setMemoryCache = (key: string, data: any, ttlMs: number = 60000) => {
  memoryCache.set(key, { data, expires: Date.now() + ttlMs })
}

const getMemoryCache = (key: string) => {
  const cached = memoryCache.get(key)
  if (cached && cached.expires > Date.now()) {
    return cached.data
  }
  memoryCache.delete(key)
  return null
}

// Clear every cached photo list for a turbine (memory + persistent + IndexedDB).
// Called after uploads/deletes/edits so screens can rely on normal caching
// instead of forcing noCache on every request.
const clearTurbinePhotoCaches = (turbineId: number) => {
  cacheManager.clearByPattern(`photos_${turbineId}_`)
  cacheManager.clearByPattern(`all_photos:${turbineId}:`)
  for (const key of memoryCache.keys()) {
    if (key.startsWith(`photos:${turbineId}:`) || key.startsWith(`all_photos:${turbineId}:`)) {
      memoryCache.delete(key)
    }
  }
  void photoDb.delete(turbineId)
}

// ---------------------------------------------------------------------------
// IndexedDB-first photo list layer with delta sync
//
// Read path:  memory cache → persistent cache → IndexedDB → API
// Sync path:  lightweight /photos/sync-state (COUNT + MAX(uploaded_at)) is
//             compared with the stored state; the full list (DB query +
//             presigned URL generation for every photo) is fetched only when
//             something changed or the stored URLs are close to expiry.
// ---------------------------------------------------------------------------

const FULL_LIST_MEMORY_TTL = 60 * 1000 // 1 minute
const FULL_LIST_PERSISTENT_TTL = 3 * 60 * 1000 // 3 minutes
const SYNC_CHECK_MIN_INTERVAL = 30 * 1000 // throttle background checks

const inFlightFullFetches = new Map<number, Promise<PhotoResponse>>()
const lastSyncChecks = new Map<number, number>()

const fullListMemoryKey = (turbineId: number) => `photos:${turbineId}:all`

const cacheFullList = (turbineId: number, result: PhotoResponse) => {
  setMemoryCache(fullListMemoryKey(turbineId), result, FULL_LIST_MEMORY_TTL)
  cacheManager.set(cacheManager.generatePhotoKey(turbineId), result, FULL_LIST_PERSISTENT_TTL)
}

const fetchSyncState = async (turbineId: number): Promise<PhotoSyncState | null> => {
  try {
    const response = await api.get(`/turbines/${turbineId}/photos/sync-state`) as any
    if (response?.success && response.data) {
      return {
        count: response.data.count ?? 0,
        maxUploadedAt: response.data.maxUploadedAt ?? null
      }
    }
  } catch (error) {
    // Bilerek sessiz: sync-state yalnızca "yerel kopya bayat mı?" sorusunu ucuza
    // yanıtlayan bir optimizasyondur. Alınamazsa null döner, çağıran da yerel kopyayı
    // servis etmeye devam eder; kullanıcı için görünür bir başarısızlık YOKTUR.
    // Gerçek liste çekimi (fetchFullPhotoListFromApi) hata verirse ORASI kullanıcıya yansır.
    console.warn('Foto sync-state alınamadı:', toApiError(error).message)
  }
  return null
}

// Raw API call for the complete photo list of a turbine
const fetchFullPhotoListFromApi = async (turbineId: number, noCache: boolean): Promise<PhotoResponse> => {
  try {
    const url = `/turbines/${turbineId}/photos${noCache ? '?noCache=true' : ''}`

    let apiResponse: PhotoResponse
    try {
      apiResponse = await api.get(url) as PhotoResponse
    } catch (wrapperError) {
      // Sarmalayıcı başarısız olduysa ham axios ile bir kez daha denenir.
      // Yetki reddinde (403) tekrar denemenin anlamı yok: aynı cevabı alırız ve
      // kullanıcı beklemek zorunda kalır. Hatayı olduğu gibi yukarı bırakıyoruz.
      const wrapped = toApiError(wrapperError)
      if (wrapped.kind === 'permission') throw wrapped

      const token = localStorage.getItem('deepwind-auth-token')
      if (!token) {
        window.location.href = '/login'
        return { success: false, message: 'Oturum süresi dolmuş' }
      }
      const response = await axios.get(`${API_BASE_URL}${url}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      apiResponse = response.data as PhotoResponse
    }

    if (apiResponse.success && Array.isArray(apiResponse.data)) {
      return {
        success: true,
        data: apiResponse.data,
        message: apiResponse.message || 'Fotoğraflar başarıyla alındı'
      }
    }

    return { success: false, message: apiResponse.message || 'Fotoğraflar yüklenemedi' }
  } catch (error) {
    // KRİTİK: Burada eskiden 403 "oturum süresi dolmuş" sayılıp token siliniyor ve
    // /login'e yönlendiriliyordu. Oysa backend 403'ü YETKİ REDDİ için kullanıyor
    // ('Bu türbine erişim yetkiniz yok', 'Hesabınız aktif değil'). Sonuç: meşru bir
    // yetki reddi kullanıcıyı sistemden atıyordu. Artık 403'te oturuma dokunmuyoruz;
    // hata normal şekilde yukarı dönüyor (ApiError.kind === 'permission').
    // Gerçek oturum düşmesi olan 401, api.ts interceptor'ında merkezi ele alınıyor.
    return {
      success: false,
      message: apiErrorMessage(error, 'Fotoğraflar alınırken hata oluştu')
    }
  }
}

// Fetch full list from API, persist to IndexedDB + caches (deduped per turbine)
const refreshFullPhotos = (turbineId: number, noCache: boolean): Promise<PhotoResponse> => {
  const existing = inFlightFullFetches.get(turbineId)
  if (existing) return existing

  const promise = (async () => {
    const result = await fetchFullPhotoListFromApi(turbineId, noCache)
    if (result.success && result.data) {
      // Store backend-authored sync state so future comparisons are exact
      const syncState = await fetchSyncState(turbineId)
      await photoDb.set({
        turbineId,
        photos: result.data,
        syncState,
        urlsFetchedAt: Date.now()
      })
      cacheFullList(turbineId, result)
    }
    return result
  })().finally(() => {
    inFlightFullFetches.delete(turbineId)
  })

  inFlightFullFetches.set(turbineId, promise)
  return promise
}

// Delta sync: re-fetch only when the backend state differs from the local copy
const syncIfChanged = async (turbineId: number, force = false): Promise<boolean> => {
  const last = lastSyncChecks.get(turbineId) || 0
  if (!force && Date.now() - last < SYNC_CHECK_MIN_INTERVAL) {
    return false
  }
  lastSyncChecks.set(turbineId, Date.now())

  const stored = await photoDb.get(turbineId)
  if (!stored) {
    return false // nothing local yet; the normal read path will populate it
  }

  const remote = await fetchSyncState(turbineId)
  if (!remote) {
    return false // sync-state unavailable; keep serving the local copy
  }

  if (syncStatesEqual(stored.syncState, remote) && isUrlsFresh(stored)) {
    return false
  }

  const result = await refreshFullPhotos(turbineId, true)
  return result.success === true
}

const getFullPhotos = async (turbineId: number, forceRefresh?: boolean): Promise<PhotoResponse> => {
  if (!forceRefresh) {
    const memoryResult = getMemoryCache(fullListMemoryKey(turbineId))
    if (memoryResult) {
      return memoryResult
    }

    const cached = cacheManager.get<PhotoResponse>(cacheManager.generatePhotoKey(turbineId))
    if (cached) {
      setMemoryCache(fullListMemoryKey(turbineId), cached, FULL_LIST_MEMORY_TTL)
      return cached
    }

    const stored = await photoDb.get(turbineId)
    if (stored && isUrlsFresh(stored)) {
      const result: PhotoResponse = {
        success: true,
        data: stored.photos,
        message: 'Fotoğraflar yerel önbellekten alındı'
      }
      cacheFullList(turbineId, result)
      // Check for remote changes in the background (throttled)
      void syncIfChanged(turbineId)
      return result
    }
  }

  return refreshFullPhotos(turbineId, !!forceRefresh)
}

export interface Turbine {
  id: number
  name?: string
  turbine_name?: string
  location: string
  city: string
  code: string
  rooms: number
  status: string
  company_id: number
  created_at?: string
  updated_at?: string 
  company_name?: string;
  photo_count_2d?: number;
  photo_count_360?: number;
  site_name?: string;
  coordinates?: string;
}

export interface PhotoVersion {
  id: number
  version_number: number
  photo_key: string
  photo_url: string
  file_size_bytes?: number
  created_at: string
  created_by?: string
  is_original?: boolean
}

export interface Photo {
  id?: number
  key: string
  url: string
  size?: number | null
  lastModified: string
  room?: string | null
  blade?: string | null  // A, B, or C for blade photos
  createdAt?: string
  upload_date?: string
  category?: string | null  // drone, operator, robot for category photos
  originalName?: string
  contentType?: string
  year?: number
  month?: number
  week?: number
  uploader_name?: string
  uploader_email?: string
  uploadedBy?: {
    name: string
    email?: string
  }
  _originalKey?: string
  latestVersion?: PhotoVersion  // For version tracking
  versionCount?: number  // # of edited versions saved for this photo (0 = original only)
}

export interface Report {
  id: number
  turbine_id: number
  report_key: string
  report_url: string
  uploaded_by: number
  uploaded_at: string
  year: number
  original_filename: string
  file_size_bytes: number
  content_type: string
  first_name?: string
  last_name?: string
}

export interface Video {
  id: number
  turbine_id: number
  video_type: string
  video_key: string
  video_url: string
  uploaded_by: number
  uploaded_at: string
  year: number
  week: number
  original_filename: string
  file_size_bytes: number
  content_type: string
  first_name?: string
  last_name?: string
}

export interface Comment {
  id: number
  turbine_id: number
  user_id: number
  comment: string
  created_at: string
  updated_at: string
  user_name: string
  user_email?: string
}

interface TurbineResponse {
  success: boolean
  data?: Turbine[]
  message?: string
  turbine?: Turbine
}

interface PhotoResponse {
  success: boolean
  data?: Photo[]
  message?: string
}

interface ReportResponse {
  success: boolean
  data?: Report[]
  message?: string
}

interface VideoResponse {
  success: boolean
  data?: Video[]
  message?: string
}

interface VideoUploadResponse {
  success: boolean
  data?: Video[]
  message?: string
  /** Dolu ise yukleme KISMEN basarisiz olmustur (bkz. UploadFailure). */
  failed?: UploadFailure[]
}

/** Kismi yukleme basarisizligi: backend hangi dosyanin neden dustugunu bildirir. */
export interface UploadFailure {
  name: string
  reason: string
}

interface PhotoUploadResponse {
  success: boolean
  data?: Photo[]
  message?: string
  /**
   * Dolu ise yukleme KISMEN basarisiz olmustur. `success:true` gelse bile
   * bu dizi bos degilse kullaniciya kayip dosyalar soylenmelidir -- eskiden
   * 5 dosyadan 3'u dusse bile yalnizca "2 dosya kaydedildi" yaziyordu.
   */
  failed?: UploadFailure[]
}

interface CommentResponse {
  success: boolean
  data?: Comment
  message?: string
}

export const turbineService = {
  // Invalidate all cached photo lists for a turbine (after upload/delete/edit)
  clearPhotoCaches(turbineId: number) {
    clearTurbinePhotoCaches(turbineId)
  },

  // Get all turbines with role-based filtering
  async getTurbines(
    user_id: number, 
    role: string, 
    company_id?: number,
    filter_company_id?: number | 'all'
  ): Promise<TurbineResponse> {
    const cacheKey = `turbines_${user_id || 'all'}_${role || 'all'}_${company_id || 'all'}_${filter_company_id || 'all'}`
    
    // Check cache first
    const cached = cacheManager.get<TurbineResponse>(cacheKey)
    if (cached) {
      return cached
    }
    
    try {
      let url = '/turbines'
      const params = new URLSearchParams()
      
      if (user_id) params.append('user_id', user_id.toString())
      if (role) params.append('role', role)
      if (company_id) params.append('company_id', company_id.toString())
      
      // Add company filter for superadmin
      if (role === 'superadmin' && filter_company_id && filter_company_id !== 'all') {
        params.append('filter_company_id', filter_company_id.toString())
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`
      }
      
      const response = await api.get(url);
      
      // API service returns the parsed response directly
      if (response.success && Array.isArray(response.data)) {
        const result = {
          success: true,
          data: response.data,
          message: response.message
        };
        
        // Cache successful response for 2 minutes
        cacheManager.set(cacheKey, result, 2 * 60 * 1000)
        return result;
      }
      
      return {
        success: false,
        message: response.message || 'Türbinler yüklenemedi'
      }
    } catch (error) {
      const message = toApiError(error).message
      console.error('Error getting turbines:', message)
      return {
        success: false,
        message: message || 'Türbinler alınırken hata oluştu'
      }
    }
  },

  async getTurbine(id: number): Promise<TurbineResponse> {
    
    try {
      const response = await api.get(`/turbines/${id}`);
      
      
      if (response.success && (response as any).turbine) {
        return {
          success: true,
          turbine: (response as any).turbine as Turbine
        };
      }
      
      return {
        success: false,
        message: response.message || 'Türbin bulunamadı'
      };
    } catch (error) {
      return {
        success: false,
        message: apiErrorMessage(error, 'Türbin yüklenirken hata oluştu')
      }
    }
  },

  async addTurbine(data: { city: string; code: string; rooms: number; location: string; company_id: number }): Promise<TurbineResponse> {

    try {
      
      const response = await api.post('/turbines', data);
      
      
      if (response.success && response.data) {
        const result: TurbineResponse = {
          success: true,
          turbine: response.data as Turbine
        };
        if (response.message) {
          result.message = response.message;
        }
        return result;
      }
      
      return {
        success: false,
        message: response.message || 'Türbin eklenemedi'
      };
    } catch (error) {
      return {
        success: false,
        message: apiErrorMessage(error, 'Türbin eklenirken hata oluştu')
      }
    }
  },

  async updateTurbine(id: number, data: { city: string; code: string; rooms: number; location: string; status: string; company_id: number }): Promise<TurbineResponse> {
    try {
      const token = localStorage.getItem('deepwind-auth-token');
      const headers: any = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const response = await axios.put(
        `${API_BASE_URL}/turbines/${id}`,
        data,
        { headers }
      );
      return response.data as TurbineResponse;
    } catch (error) {
      // Ham axios çağrısı: ağ/zaman aşımı hatalarında `error.response` yoktur ve eski kod
      // bu durumda "bilinmeyen hata" diyordu. toApiError bağlantı sorununu da ayırt eder.
      const message = toApiError(error).message;
      console.error('TurbineService.updateTurbine error:', message);
      return { success: false, message: message || 'Türbin güncellenirken hata oluştu' };
    }
  },

  async deleteTurbine(id: number, user_id: number, role: string, company_id: number): Promise<TurbineResponse> {
    try {
      const token = localStorage.getItem('deepwind-auth-token');
      const headers: any = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      const response = await axios.delete(
        `${API_BASE_URL}/turbines/${id}`,
        {
          headers,
          data: {
            user_id,
            role,
            company_id
          }
        }
      );
      
      return response.data as TurbineResponse;
    } catch (error) {
      return { success: false, message: apiErrorMessage(error, 'Türbin silinirken hata oluştu') };
    }
  },

  // Direct upload method for large files (50MB+ photos, 50GB+ videos)
  async uploadPhotosDirectly(turbineId: number, room: number | string, files: File[], year?: number, blade?: string): Promise<PhotoUploadResponse> {
    try {
      // Step 1: Get presigned URLs
      const fileInfos = files.map(file => ({
        name: file.name,
        size: file.size,
        type: file.type
      }));
      
      const token = localStorage.getItem('deepwind-auth-token');
      if (!token) {
        // Ham Error yerine ApiError: aksi halde kullanıcı İngilizce
        // "Authentication token not found" metnini görüyordu.
        throw new ApiError('auth');
      }
      
      console.log(`🔧 Requesting presigned URLs for ${files.length} files`);
      
      const presignedResponse = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL || 'https://deepwind-backend.vercel.app/api'}/turbines/${turbineId}/presigned-upload`,
        {
          files: fileInfos,
          room: room.toString(),
          year,
          blade
        },
        {
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 60000 // 1 minute timeout for getting presigned URLs
        }
      );
      
      console.log(`✅ Got presigned URLs response:`, presignedResponse.data);
      
      if (!presignedResponse.data?.success) {
        // Backend mesajı kutsaldır: serverMessage olarak taşınır, jenerik metinle ezilmez.
        throw new ApiError('server', {
          serverMessage: presignedResponse.data?.message || 'Yükleme adresi alınamadı.'
        });
      }
      
      const presignedUrls = presignedResponse.data.presignedUrls;
      
      // Step 2: Upload files directly to S3
      const uploadPromises = presignedUrls.map(async (urlInfo: any, index: number) => {
        const file = files[index];
        
        if (!file) {
          // Programlama hatası (dizi hizası bozuk). toApiError bunu 'unknown' sayar ve
          // İngilizce metni kullanıcıya sızdırmadan jenerik TR mesaj üretir.
          throw new Error(`File at index ${index} is undefined`);
        }
        
        try {
          console.log(`🚀 Starting direct upload for ${file.name} (${(file.size / 1024 / 1024).toFixed(1)}MB)`);
          
          const uploadResponse = await axios.put(urlInfo.presignedUrl, file, {
            headers: {
              'Content-Type': file.type,
            },
            timeout: 0, // No timeout for large files
            maxContentLength: Infinity,
            maxBodyLength: Infinity,
            onUploadProgress: (progressEvent) => {
              if (progressEvent.total && progressEvent.loaded) {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                console.log(`📈 Upload progress for ${file.name}: ${percentCompleted}%`);
              }
            }
          });
          
          if (uploadResponse.status === 200) {
            console.log(`✅ Direct upload completed for ${file.name}`);
            return {
              key: urlInfo.key,
              originalName: file.name,
              size: file.size,
              type: file.type
            };
          } else {
            // HTTP kodu kullanıcıya dökülmesin: sebep depolama servisi, çözüm tekrar denemek.
            throw new ApiError('server', {
              status: uploadResponse.status,
              serverMessage: 'Dosya depolama servisine yüklenemedi.'
            });
          }
        } catch (uploadError) {
          // Süresi dolmuş presigned URL'de Wasabi 403 + XML gövde döner; eski kod
          // error.message'ı kullandığı için kullanıcı ham "Request failed with status
          // code 403" metnini görüyordu. toApiError XML'i reddedip TR metin üretir.
          console.error(`❌ Direct upload failed for ${file.name}:`, uploadError);
          throw withFileName(
            file.name,
            toStoragePutError(uploadError, 'Yükleme adresinin süresi doldu. Lütfen tekrar yüklemeyi deneyin.')
          );
        }
      });
      
      const uploadResults = await Promise.all(uploadPromises);
      
      // Step 3: Confirm uploads with backend
      const confirmResponse = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL || 'https://deepwind-backend.vercel.app/api'}/turbines/${turbineId}/confirm-upload`,
        {
          uploads: uploadResults,
          room: room.toString(),
          year,
          blade
        },
        {
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 60000 // 1 minute timeout for confirmation
        }
      );
      
      console.log(`🎉 Direct upload completed for ${files.length} files`);

      // Invalidate cached photo lists so the new photos show up immediately
      clearTurbinePhotoCaches(turbineId)

      return confirmResponse.data;

    } catch (error) {
      console.error('❌ Direct upload error:', error);

      return {
        success: false,
        message: uploadErrorMessage(error, 'Dosya çok büyük. Lütfen daha küçük dosyalar deneyin.')
          || 'Büyük dosyalar yüklenirken hata oluştu'
      };
    }
  },

  // Photo management methods
  async uploadPhotos(turbineId: number, room: number | string, files: File[], year?: number, blade?: string): Promise<PhotoUploadResponse> {
    
    // Validate file names
    const invalidFiles = files.filter(file => {
      const fileName = file.name
      // Check for invalid characters: spaces, parentheses, special characters
      const invalidChars = /[^A-Z0-9_\-.]/i.test(fileName)
      return invalidChars
    })

    if (invalidFiles.length > 0) {
      const invalidNames = invalidFiles.map(f => f.name).join(', ')
      return {
        success: false,
        message: `Hatalı dosya adları: ${invalidNames}\n\nDosya Adı Standartları:\n• Sadece büyük/küçük harf, rakam, _ ve - kullanılabilir\n• Boşluk, parantez ve özel karakterler kullanılamaz. \n• Türkçe karakterler kullanılamaz.`
      }
    }

    // Check for duplicate filenames
    try {
      const existingPhotos = await this.getPhotos(turbineId)
      if (existingPhotos.success && existingPhotos.data) {
        const existingFilenames = existingPhotos.data.map(photo => {
          // Extract filename from photo key
          const keyParts = photo.key.split('/')
          return keyParts[keyParts.length - 1]
        })
        
        const duplicateFiles = files.filter(file => 
          existingFilenames.some(existing => existing === file.name)
        )
        
        if (duplicateFiles.length > 0) {
          const duplicateNames = duplicateFiles.map(f => f.name).join(', ')
          return {
            success: false,
            message: `Aynı isimde fotoğraflar zaten mevcut: ${duplicateNames}\n\nLütfen farklı isimler kullanın veya mevcut fotoğrafları silin.`
          }
        }
      }
    } catch (error) {
      // Bilerek sessiz: bu yalnızca bir ÖN kontroldür (kullanıcıyı boşuna yüklemeden
      // korur). Başarısız olması yüklemenin başarısız olacağı anlamına gelmez, o yüzden
      // akışı kesmiyor ve kullanıcıya hata göstermiyoruz - göstersek meşru bir yüklemeyi
      // hata gibi sunmuş olurduk. Aynı isim gerçekten çakışıyorsa backend zaten reddeder
      // ve o hata aşağıdaki catch üzerinden kullanıcıya görünür.
      console.warn('Duplicate check failed, continuing with upload:', toApiError(error).message)
    }
    
    try {
      const formData = new FormData()
      formData.append('room', room.toString())
        if (year) {
          formData.append('year', year.toString())
        }
        if (blade) {
          formData.append('blade', blade)
        }
      
      files.forEach((file) => {
        formData.append('photos', file)
      })
      
      // Get fresh auth token and validate it
      const token = localStorage.getItem('deepwind-auth-token')
      
      
      if (!token) {
        throw new ApiError('auth')
      }

      // Validate token before large upload
      try {
        await axios.get(
          `${import.meta.env.VITE_API_BASE_URL || 'https://deepwind-backend.vercel.app/api'}/auth/verify`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } catch (validationError) {
        // Eski kod her başarısızlığı "Session expired" sayıp İngilizce metin fırlatıyordu.
        // Bağlantı sorununu oturum düşmesinden ayırıyoruz ki kullanıcı doğru şeyi denesin.
        const verifyError = toApiError(validationError)
        throw verifyError.kind === 'network' || verifyError.kind === 'timeout'
          ? verifyError
          : new ApiError('auth')
      }
      
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`
      }
      // Let axios set Content-Type automatically for FormData
      
      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL || 'https://deepwind-backend.vercel.app/api'}/turbines/${turbineId}/photos`,
        formData,
        { 
          headers,
          timeout: 300000, // 5 minutes timeout to work with Vercel limits
          maxContentLength: 55 * 1024 * 1024, // 55MB limit to allow for 50MB files + overhead
          maxBodyLength: 55 * 1024 * 1024 // 55MB limit per request
        }
      )

      // Invalidate cached photo lists so the new photos show up immediately
      clearTurbinePhotoCaches(turbineId)

      return response.data as PhotoUploadResponse
    } catch (error) {
      // NOT: bu multipart metodun src/ icinde CAGIRANI YOK; tum ekranlar
      // uploadPhotosDirectly (presigned PUT) kullaniyor. Onceki yorum "cagiran ekran
      // 413 metnini gorunce dosyalari tek tek yuklemeye geciyor" diyordu; boyle bir
      // cagiran da, bu metni eslesitiren bir kod da yok.
      return {
        success: false,
        message: uploadErrorMessage(error, 'Dosya çok büyük. Lütfen daha küçük dosyalar deneyin.')
      }
    }
  },

  // Photos are served IndexedDB-first: the full list for a turbine lives in
  // IndexedDB and is kept current via delta sync (see getFullPhotos below).
  // Room filtering happens client-side, so switching rooms/blades never hits
  // the backend.
  async getPhotos(turbineId: number, room?: number, forceRefresh?: boolean): Promise<PhotoResponse> {
    const full = await getFullPhotos(turbineId, forceRefresh)
    if (!full.success || !full.data || room === undefined || room === null) {
      return full
    }

    const targetRoom = Number.parseInt(String(room), 10)
    const filtered = full.data.filter(photo => {
      if (photo.room === null || photo.room === undefined) return false
      return Number.parseInt(String(photo.room), 10) === targetRoom
    })

    return { success: true, data: filtered, ...(full.message ? { message: full.message } : {}) }
  },

  // Same IndexedDB-first list; year/week filtering happens client-side.
  async getAllPhotos(turbineId: number, year?: number, week?: number, forceRefresh?: boolean): Promise<PhotoResponse> {
    const full = await getFullPhotos(turbineId, forceRefresh)
    if (!full.success || !full.data) {
      return full
    }

    let data = full.data
    if (year) {
      data = data.filter(photo => photo.year === year)
    }
    if (week) {
      data = data.filter(photo => photo.week === week)
    }

    if (data === full.data) return full
    return { success: true, data, ...(full.message ? { message: full.message } : {}) }
  },

  // Delta sync check: compares the lightweight backend sync-state against the
  // IndexedDB copy and re-syncs the list only when something actually changed.
  // Returns true when the local copy was updated.
  async checkPhotoUpdates(turbineId: number): Promise<boolean> {
    return syncIfChanged(turbineId, true)
  },

  // Delete photo
  async deletePhoto(turbineId: number, photoKey: string): Promise<{ success: boolean, message?: string }> {
    try {
      // Use query parameter instead of path parameter to avoid URL encoding issues
      const response = await api.delete(`/turbines/${turbineId}/photos?key=${encodeURIComponent(photoKey)}`)
      
      if (response.success) {
        // Clear all cached photo lists for this turbine (memory + persistent, all rooms)
        clearTurbinePhotoCaches(turbineId)

        // Clear photo image cache to prevent showing deleted images
        photoImageCache.clearCache()
        
        return {
          success: true,
          message: response.message || 'Fotoğraf başarıyla silindi'
        }
      }
      
      return {
        success: false,
        message: response.message || 'Fotoğraf silinemedi'
      }
    } catch (error) {
      // Eski hâlde hata tamamen yok sayılıyordu: backend "Bu fotoğrafı silme yetkiniz yok"
      // dese bile kullanıcı hep aynı jenerik metni görüyordu.
      return {
        success: false,
        message: apiErrorMessage(error, 'Fotoğraf silinirken hata oluştu')
      }
    }
  },

  // Comment functions
  async getComments(turbineId: number): Promise<{ success: boolean; data?: Comment[]; message?: string }> {
    try {
      const response = await api.get(`/turbines/${turbineId}/comments`) as any
      // success:false BİR HATADIR: eski kod bunu success:true + boş liste olarak
      // gösteriyordu, yani hata "hiç yorum yok" gibi görünüyordu.
      if (response?.success === false) {
        return {
          success: false,
          message: response.message || 'Yorumlar alınırken hata oluştu'
        }
      }
      return {
        success: true,
        data: (response.data || []) as Comment[],
        message: response.message || 'Yorumlar başarıyla alındı'
      }
    } catch (error) {
      const message = toApiError(error).message
      console.error('Get comments error:', message)
      return {
        success: false,
        message: message || 'Yorumlar alınırken hata oluştu'
      }
    }
  },

  async addComment(turbineId: number, comment: string): Promise<{ success: boolean; data?: Comment; message?: string; comment?: Comment }> {
    try {
      const response = await api.post(`/turbines/${turbineId}/comments`, { comment }) as any
      // success:false'ı başarı gibi göstermeyelim: yorum kaydedilmediği hâlde
      // ekranda "eklendi" yazıyordu.
      if (response?.success === false) {
        return {
          success: false,
          message: response.message || 'Yorum eklenirken hata oluştu'
        }
      }
      return {
        success: true,
        data: response.data as Comment,
        message: response.message || 'Yorum başarıyla eklendi'
      }
    } catch (error) {
      const message = toApiError(error).message
      console.error('Add comment error:', message)
      return {
        success: false,
        message: message || 'Yorum eklenirken hata oluştu'
      }
    }
  },

  async deleteComment(turbineId: number, commentId: number): Promise<{ success: boolean; message?: string }> {
    try {
      await api.delete(`/turbines/${turbineId}/comments/${commentId}`)
      return { success: true }
    } catch (error) {
      const message = toApiError(error).message
      console.error('Delete comment error:', message)
      return {
        success: false,
        message: message || 'Yorum silinirken hata oluştu'
      }
    }
  },

  // Get reports for turbine
  async getReports(turbineId: number, year?: number, week?: number): Promise<ReportResponse> {
    try {
      const params = new URLSearchParams()
      if (year) {
        params.append('year', year.toString())
      }
      if (week) {
        params.append('week', week.toString())
      }

      const apiResponse = await api.get(`/turbines/${turbineId}/reports?${params.toString()}`)

      return apiResponse as ReportResponse
    } catch (error) {
      return {
        success: false,
        message: apiErrorMessage(error, 'Raporlar getirilirken hata oluştu')
      }
    }
  },

  // Delete report
  async deleteReport(turbineId: number, reportId: number): Promise<{ success: boolean; message?: string }> {
    try {
      
      const response = await api.delete(`/turbines/${turbineId}/reports/${reportId}`)
      
      
      // Check if response has success property
      if (response && typeof response === 'object' && 'success' in response) {
        return {
          success: response.success,
          // Basari ve hata metinleri AYRI: mesajsiz bir {success:false} gelirse
          // cagiran ekran hata kutusunda "Rapor basariyla silindi" yaziyordu.
          message: response.message || (response.success ? 'Rapor başarıyla silindi' : 'Rapor silinemedi')
        }
      }
      
      // If no success property, assume success
      return {
        success: true,
        message: 'Rapor başarıyla silindi'
      }
    } catch (error) {
      return {
        success: false,
        message: apiErrorMessage(error, 'Rapor silinirken hata oluştu')
      }
    }
  },

  // Video management methods
  async getVideos(turbineId: number, year?: number, week?: number, type?: string): Promise<VideoResponse> {
    try {
      const params = new URLSearchParams()
      if (year) params.append('year', year.toString())
      if (week) params.append('week', week.toString())
      if (type) params.append('type', type)

      const queryString = params.toString()
      const url = `/turbines/${turbineId}/videos${queryString ? `?${queryString}` : ''}`

      const response = await api.get(url)

      if (response && typeof response === 'object' && 'success' in response) {
        return {
          success: response.success,
          data: response.data as Video[],
          message: response.message
        }
      }

      return {
        success: false,
        message: 'Videolar alınırken hata oluştu'
      }
    } catch (error) {
      return {
        success: false,
        message: apiErrorMessage(error, 'Videolar alınırken hata oluştu')
      }
    }
  },

  async uploadVideosDirectly(turbineId: number, videoType: string, files: File[], year?: number): Promise<VideoUploadResponse> {
    try {
      // Step 1: Get presigned URLs
      const fileInfos = files.map(file => ({
        name: file.name,
        size: file.size,
        type: file.type
      }))

      const token = localStorage.getItem('deepwind-auth-token')
      if (!token) {
        throw new ApiError('auth')
      }

      console.log(`🔧 Requesting presigned URLs for ${files.length} video file(s)`)

      const presignedResponse = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL || 'https://deepwind-backend.vercel.app/api'}/turbines/${turbineId}/videos/presigned-upload`,
        {
          files: fileInfos,
          videoType,
          year
        },
        {
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 60000 // 1 minute timeout for getting presigned URLs
        }
      )

      console.log(`✅ Got presigned URLs response:`, presignedResponse.data)

      if (!presignedResponse.data?.success) {
        throw new ApiError('server', {
          serverMessage: presignedResponse.data?.message || 'Yükleme adresi alınamadı.'
        })
      }

      const presignedUrls = presignedResponse.data.presignedUrls

      // Step 2: Upload files directly to S3
      const uploadPromises = presignedUrls.map(async (urlInfo: any, index: number) => {
        const file = files[index]

        if (!file) {
          // Programlama hatası; kullanıcıya toApiError üzerinden jenerik TR metin gider.
          throw new Error(`File at index ${index} is undefined`)
        }

        try {
          console.log(`🚀 Starting direct upload for ${file.name} (${(file.size / 1024 / 1024).toFixed(1)}MB)`)

          const uploadResponse = await axios.put(urlInfo.presignedUrl, file, {
            headers: {
              'Content-Type': file.type,
            },
            timeout: 0, // No timeout for large videos
            maxContentLength: Infinity,
            maxBodyLength: Infinity,
            onUploadProgress: (progressEvent) => {
              if (progressEvent.total && progressEvent.loaded) {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
                console.log(`📈 Upload progress for ${file.name}: ${percentCompleted}%`)
              }
            }
          })

          if (uploadResponse.status === 200) {
            console.log(`✅ Direct upload completed for ${file.name}`)
            return {
              key: urlInfo.key,
              originalName: file.name,
              size: file.size,
              type: file.type
            }
          } else {
            throw new ApiError('server', {
              status: uploadResponse.status,
              serverMessage: 'Dosya depolama servisine yüklenemedi.'
            })
          }
        } catch (uploadError) {
          console.error(`❌ Direct upload failed for ${file.name}:`, uploadError)
          throw withFileName(
            file.name,
            toStoragePutError(uploadError, 'Yükleme adresinin süresi doldu. Lütfen tekrar yüklemeyi deneyin.')
          )
        }
      })

      const uploadResults = await Promise.all(uploadPromises)

      // Step 3: Confirm uploads with backend
      const confirmResponse = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL || 'https://deepwind-backend.vercel.app/api'}/turbines/${turbineId}/videos/confirm-upload`,
        {
          uploads: uploadResults,
          videoType,
          year
        },
        {
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 60000 // 1 minute timeout for confirmation
        }
      )

      console.log(`🎉 Direct video upload completed for ${files.length} file(s)`)
      return confirmResponse.data

    } catch (error) {
      console.error('❌ Direct video upload error:', error)

      return {
        success: false,
        message: uploadErrorMessage(error, 'Video dosyası çok büyük. Lütfen daha küçük dosyalar deneyin.')
          || 'Video yüklenirken hata oluştu'
      }
    }
  },

  async deleteVideo(turbineId: number, videoId: number): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await api.delete(`/turbines/${turbineId}/videos/${videoId}`)

      // Check if response has success property
      if (response && typeof response === 'object' && 'success' in response) {
        return {
          success: response.success,
          message: response.message || (response.success ? 'Video başarıyla silindi' : 'Video silinemedi')
        }
      }

      // If no success property, assume success
      return {
        success: true,
        message: 'Video başarıyla silindi'
      }
    } catch (error) {
      return {
        success: false,
        message: apiErrorMessage(error, 'Video silinirken hata oluştu')
      }
    }
  },

  // Update comment
  async updateComment(turbineId: number, commentId: number, comment: string): Promise<CommentResponse> {
    try {
      const response = await api.put(`/turbines/${turbineId}/comments/${commentId}`, {
        comment: comment.trim()
      })
      
      if (response && typeof response === 'object' && 'success' in response) {
        return {
          success: response.success,
          message: response.message || (response.success ? 'Yorum başarıyla güncellendi' : 'Yorum güncellenemedi'),
          data: response.data as Comment
        }
      }
      
      return {
        success: true,
        message: 'Yorum başarıyla güncellendi'
      }
    } catch (error) {
      return {
        success: false,
        message: apiErrorMessage(error, 'Yorum güncellenirken hata oluştu')
      }
    }
  },

  // Save an edited photo as a new version using the direct-to-Wasabi flow.
  // The image is uploaded with a presigned PUT, bypassing the Vercel function's
  // 4.5MB request body limit that broke saves of large drone/360 photos.
  async saveEditedPhotoVersion(
    turbineId: number,
    photoId: number,
    imageBlob: Blob,
    contentType: string = 'image/jpeg'
  ): Promise<{ success: boolean; message?: string; data?: PhotoVersion }> {
    try {
      const token = localStorage.getItem('deepwind-auth-token')
      if (!token) {
        throw new ApiError('auth')
      }

      // Step 1: presign — backend computes the next version number + upload URL
      const presignResponse = await axios.post(
        `${API_BASE_URL}/turbines/${turbineId}/photos/${photoId}/versions/presign`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` }, timeout: 60000 }
      )
      if (!presignResponse.data?.success || !presignResponse.data.data) {
        throw new ApiError('server', {
          serverMessage: presignResponse.data?.message || 'Yükleme adresi alınamadı.'
        })
      }
      const { presignedUrl, versionedKey, versionNumber } = presignResponse.data.data

      // Step 2: upload the edited image directly to Wasabi
      try {
        await axios.put(presignedUrl, imageBlob, {
          headers: { 'Content-Type': contentType },
          timeout: 0,
          maxContentLength: Infinity,
          maxBodyLength: Infinity
        })
      } catch (uploadError) {
        throw toStoragePutError(uploadError, 'Yükleme adresinin süresi doldu. Lütfen tekrar kaydetmeyi deneyin.')
      }

      // Step 3: confirm — record the new version in the database
      const confirmResponse = await axios.post(
        `${API_BASE_URL}/turbines/${turbineId}/photos/${photoId}/versions/confirm`,
        { versionedKey, versionNumber, fileSize: imageBlob.size, contentType },
        { headers: { 'Authorization': `Bearer ${token}` }, timeout: 60000 }
      )
      if (!confirmResponse.data?.success) {
        throw new ApiError('server', {
          serverMessage: confirmResponse.data?.message || 'Versiyon kaydedilemedi.'
        })
      }

      return {
        success: true,
        message: confirmResponse.data.message || 'Yeni versiyon olarak kaydedildi',
        data: confirmResponse.data.data as PhotoVersion
      }
    } catch (error) {
      console.error('❌ Save edited photo version error:', error)
      return {
        success: false,
        message: apiErrorMessage(error, 'Fotoğraf kaydedilemedi')
      }
    }
  },

  // Get all versions of a photo
  async getPhotoVersions(turbineId: number, photoId: number): Promise<{ success: boolean; data?: { original: PhotoVersion; versions: PhotoVersion[] }; message?: string }> {
    try {
      const token = localStorage.getItem('deepwind-auth-token')
      if (!token) {
        throw new ApiError('auth')
      }

      // Cache-busting parametresi ekle - her seferinde fresh signed URL almak için
      const cacheBuster = Date.now()
      const response = await axios.get(
        `${import.meta.env.VITE_API_BASE_URL || 'https://deepwind-backend.vercel.app/api'}/turbines/${turbineId}/photos/${photoId}/versions?_cb=${cacheBuster}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          },
          timeout: 30000
        }
      )

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data
        }
      } else {
        throw new ApiError('server', {
          serverMessage: response.data?.message || 'Versiyonlar yüklenemedi.'
        })
      }
    } catch (error) {
      console.error('❌ Get photo versions error:', error)
      return {
        success: false,
        message: apiErrorMessage(error, 'Versiyonlar yüklenirken hata oluştu')
      }
    }
  },

  async deletePhotoVersion(turbineId: number, photoId: number, versionId: number): Promise<{ success: boolean; message?: string }> {
    try {
      const token = localStorage.getItem('deepwind-auth-token')
      if (!token) {
        throw new ApiError('auth')
      }

      const endpoint = `${import.meta.env.VITE_API_BASE_URL || 'https://deepwind-backend.vercel.app/api'}/turbines/${turbineId}/photos/${photoId}/versions/${versionId}`
      console.log(`🗑️ Deleting photo version: turbineId=${turbineId}, photoId=${photoId}, versionId=${versionId}`)
      console.log('Delete endpoint:', endpoint)

      const response = await axios.delete(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` },
        timeout: 30000
      })

      console.log('Delete response status:', response.status)
      console.log('Delete response data:', response.data)

      if (response.data.success) {
        console.log(`✅ Photo version ${versionId} deleted successfully`)
        return {
          success: true
        }
      } else {
        throw new ApiError('server', {
          serverMessage: response.data?.message || 'Versiyon silinemedi.'
        })
      }
    } catch (error) {
      console.error('❌ Delete photo version error:', error)
      return {
        success: false,
        message: apiErrorMessage(error, 'Versiyon silinirken hata oluştu')
      }
    }
  }
} 